import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trash, Minus, Plus, ShoppingCart, LockSimple, CircleNotch } from "@phosphor-icons/react";
import { useCart, unitPrice, maxFor } from "@/lib/cart";
import { startCheckout } from "@/lib/api";
import { getToken } from "@/lib/auth";

export default function SupplyCart() {
  const { items, setQty, removeItem, subtotal, count } = useCart();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const { checkout_url } = await startCheckout({
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
        customer_email: email || "",
      }, getToken());
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="pt-28 md:pt-32 min-h-[70vh]" data-testid="supply-cart-page">
      <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-8">
        <Link to="/supply" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-navy-900 mb-6"><ArrowLeft size={16} /> Continue shopping</Link>
        <h1 className="font-display font-black text-3xl md:text-4xl text-navy-900 tracking-tight">Your cart</h1>

        {items.length === 0 ? (
          <div data-testid="cart-empty" className="text-center py-20 border border-dashed border-slate-300 rounded-2xl mt-8">
            <ShoppingCart size={44} weight="thin" className="text-navy-300 mx-auto" />
            <h3 className="font-display font-black text-xl text-navy-900 mt-4">Your cart is empty.</h3>
            <Link to="/supply" className="inline-flex items-center gap-2 mt-5 rounded-full bg-navy-900 text-white px-6 py-3 font-bold hover:bg-navy-600 transition-colors">Browse the catalog</Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_360px] gap-8 mt-8">
            {/* Items */}
            <div className="space-y-3">
              {items.map((it, i) => {
                const ppu = unitPrice(it);
                const discounted = ppu < (it.price || 0);
                const cap = maxFor(it);
                const atMax = Number.isFinite(cap) && it.quantity >= cap;
                return (
                <div key={it.product_id} data-testid={`cart-item-${i}`} className="flex flex-wrap items-center gap-x-4 gap-y-3 bg-white border border-slate-200 rounded-2xl p-4">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-slate-50 border border-slate-200 grid place-items-center overflow-hidden shrink-0">
                    {it.image ? <img src={it.image} alt="" className="w-full h-full object-contain p-1.5" /> : <ShoppingCart size={22} className="text-slate-300" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <Link to={`/supply/${it.product_id}`} className="font-display font-bold text-navy-900 hover:underline line-clamp-2">{it.name}</Link>
                    <p className="text-sm text-slate-500 mt-0.5">
                      ${Number(ppu).toFixed(2)}{it.unit ? ` / ${it.unit}` : ""}
                      {discounted && <span className="ml-2 text-xs font-semibold text-green-600">volume price</span>}
                    </p>
                    {atMax && <p data-testid={`cart-max-note-${i}`} className="text-xs font-semibold text-orange-600 mt-0.5">Only {cap} in stock</p>}
                  </div>
                  <div className="inline-flex items-center rounded-full border border-slate-300 overflow-hidden shrink-0">
                    <button onClick={() => setQty(it.product_id, it.quantity - 1)} aria-label="Decrease" data-testid={`cart-minus-${i}`} className="w-9 h-10 grid place-items-center text-navy-900 hover:bg-slate-100"><Minus size={14} weight="bold" /></button>
                    <span data-testid={`cart-qty-${i}`} className="w-10 text-center font-bold text-navy-900">{it.quantity}</span>
                    <button onClick={() => setQty(it.product_id, it.quantity + 1)} disabled={atMax} aria-label="Increase" data-testid={`cart-plus-${i}`} className="w-9 h-10 grid place-items-center text-navy-900 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"><Plus size={14} weight="bold" /></button>
                  </div>
                  <div className="w-20 text-right font-display font-black text-navy-900 shrink-0 ml-auto">${(ppu * it.quantity).toFixed(2)}</div>
                  <button onClick={() => removeItem(it.product_id)} aria-label="Remove" data-testid={`cart-remove-${i}`} className="w-9 h-9 grid place-items-center rounded-full border border-red-300 text-red-600 hover:bg-red-500 hover:text-white transition-colors shrink-0"><Trash size={16} /></button>
                </div>
              );})}
            </div>

            {/* Summary */}
            <div className="lg:sticky lg:top-28 h-fit">
              <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-[0_12px_40px_rgb(10,17,40,0.06)]">
                <h3 className="font-display font-black text-xl text-navy-900">Order summary</h3>
                <div className="flex justify-between text-sm text-slate-600 mt-4"><span>Items ({count})</span><span className="font-semibold text-navy-900">${subtotal.toFixed(2)}</span></div>
                <p className="text-xs text-slate-400 mt-1">Tax calculated at checkout.</p>
                <div className="border-t border-slate-100 my-4" />
                <label className="block text-sm font-semibold text-navy-900 mb-1.5">Email for receipt (optional)</label>
                <input data-testid="cart-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="w-full rounded-xl border border-slate-300 px-4 py-3 text-navy-900 focus:outline-none focus:ring-2 focus:ring-navy-600" />
                <button onClick={checkout} disabled={loading} data-testid="cart-checkout" className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy-900 text-white py-4 font-bold hover:bg-navy-600 transition-colors disabled:opacity-60">
                  {loading ? <><CircleNotch size={18} className="animate-spin" /> Redirecting…</> : <><LockSimple size={18} weight="fill" /> Checkout — ${subtotal.toFixed(2)}</>}
                </button>
                <p className="text-[11px] text-slate-400 text-center mt-3">Secure payment via Square. Test card 4111 1111 1111 1111.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
